import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Alert, Button, Icon } from '@canaveral/components';
import IconV2 from '../../components/stateless/Icon';
import ImageUploadRow from '../../../../src/web/components/FileUpload/components/ImageUploadRow';
import VideoUploadRow from '../../../../src/web/components/FileUpload/components/VideoUploadRow';
import { INPUT_PERMITTED_IMAGES_TYPES, resolveContentUrl } from '../../../assets';
import { FLIPPER_VOYAGER_MEDIA_GALLERY } from '../../../../constants/flippers';
import useFlipper from '../../hooks/useFlipper';
import Video from './Video';
import styles from './styles.scss';

export const ALIGNMENTS = {
  CENTER: 'center',
  LEFT: 'left',
  RIGHT: 'right',
};

const propTypes = {
  alignment: PropTypes.string,
  alignmentEnabled: PropTypes.bool,
  error: PropTypes.string,
  handleAlignmentChange: PropTypes.func,
  handleAltChange: PropTypes.func.isRequired,
  handleDecorativeChange: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleHrefChange: PropTypes.func.isRequired,
  handleMove: PropTypes.func.isRequired,
  handleTitleChange: PropTypes.func.isRequired,
  handleWidthChange: PropTypes.func,
  handleHeightChange: PropTypes.func,
  handleVideoEmbedChange: PropTypes.func,
  videoUploadEnabled: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.shape({
    mediumId: PropTypes.string,
    file: PropTypes.string,
    alt: PropTypes.string,
    title: PropTypes.string,
    link: PropTypes.string,
    target: PropTypes.string,
  })).isRequired,
  limit: PropTypes.number,
  loading: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  setImages: PropTypes.func.isRequired,
  setShowGallery: PropTypes.func.isRequired,
  setTarget: PropTypes.func.isRequired,
};

const defaultProps = {
  alignment: undefined,
  alignmentEnabled: undefined,
  error: undefined,
  loading: undefined,
  videoUploadEnabled: false,
  handleAlignmentChange: undefined,
  handleVideoEmbedChange: undefined,
  handleWidthChange: undefined,
  handleHeightChange: undefined,
  limit: 5,
};

const Media = ({
  alignmentEnabled,
  alignment,
  error,
  handleAlignmentChange,
  handleAltChange,
  handleDecorativeChange,
  handleDelete,
  handleHrefChange,
  handleMove,
  handleTitleChange,
  handleWidthChange,
  handleHeightChange,
  handleVideoEmbedChange,
  videoUploadEnabled,
  images,
  limit,
  loading,
  onUpload,
  setImages,
  setShowGallery,
  setTarget,
}) => {
  const uploadRef = useRef();
  const mediaGalleryEnabled = useFlipper(FLIPPER_VOYAGER_MEDIA_GALLERY);
  const [showVideo, setShowVideo] = useState();

  const limitMsg = `This widget has a limit of ${limit} images. You must delete an image before uploading a new one.`;

  return (
    <>
      {error && (
        <Alert
          className={styles.alert}
          context="danger"
        >
          <Icon
            id="warning"
            context="danger"
            label="error"
          />
          <span className={styles.alertText}>{error}</span>
        </Alert>
      )}
      <div className={styles.buttonContainer}>
        <Button
          className={styles.button}
          context="neutral"
          icon="upload"
          onClick={() => uploadRef.current.click()}
          disabled={loading || images.length === limit}
        >
          Upload Image
        </Button>
        {mediaGalleryEnabled && (
          <Button
            className={styles.button}
            context="neutral"
            disabled={loading || images.length === limit}
            icon="images"
            onClick={() => setShowGallery(true)}
          >
            Select from Gallery
          </Button>
        )}
        {videoUploadEnabled && (
          <Button
            className={styles.button}
            context="neutral"
            icon="social-youtube"
            onClick={() => setShowVideo(true)}
            disabled={loading || images.length === limit}
          >
            Add Video
          </Button>
        )}
        {alignmentEnabled ? (
          <Button.Group className={styles.alignmentGroup}>
            <Button
              className={styles.button}
              onClick={() => handleAlignmentChange(ALIGNMENTS.LEFT)}
              active={alignment === ALIGNMENTS.LEFT}
              context="neutral"
              icon="align-left"
              label="Left Align"
            />
            <Button
              className={styles.button}
              onClick={() => handleAlignmentChange(ALIGNMENTS.CENTER)}
              active={alignment === ALIGNMENTS.CENTER}
              context="neutral"
              icon="align-center"
              label="Center Align"
            />
            <Button
              className={styles.button}
              onClick={() => handleAlignmentChange(ALIGNMENTS.RIGHT)}
              active={alignment === ALIGNMENTS.RIGHT}
              context="neutral"
              icon="align-right"
              label="Right Align"
            />
          </Button.Group>
        ) : null}
        <input
          accept={INPUT_PERMITTED_IMAGES_TYPES}
          className={styles.uploadInput}
          ref={uploadRef}
          type="file"
          onChange={onUpload}
        />
        {showVideo && (
          <Video images={images} setImages={setImages} setShowVideo={setShowVideo} />
        )}
        {images.length === limit && (
          <div className={styles.tooltipContainer}>
            <OverlayTrigger
              placement="right"
              overlay={(
                <Tooltip>
                  <div className={styles.tooltipMessage}>
                    {limitMsg}
                  </div>
                </Tooltip>
              )}
            >
              <span>
                <IconV2 id="tool-tip" label="Link Info" />
              </span>
            </OverlayTrigger>
          </div>
        )}
        <div className={styles.limit}>
          {`${images.length}/${limit} uploaded`}
        </div>
      </div>
      <div className={styles.filelist}>
        <Alert
          className={styles.alertMetadata}
          context="primary"
        >
          <Icon id="info" context="primary" label="info" />
          <span className={styles.alertText}>
            Editing the title, decorative only status, or alt text here will also update it
            everywhere the image is used.
          </span>
        </Alert>
        <div className={styles.fileListItems}>
          {images.map((file, index) => {
            if (!file) {
              return false;
            }

            const editorProps = {
              index,
              handleDelete: handleDelete(index),
              moveFileRowUp: index !== 0 && handleMove(index, -1),
              moveFileRowDown: index !== (images.length - 1) && handleMove(index, 1),
              setTarget,
              handleAltChange: handleAltChange(index),
              handleDecorativeChange: handleDecorativeChange && handleDecorativeChange(index),
              handleTitleChange: handleTitleChange(index),
              handleHrefChange: handleHrefChange(index),
              handleWidthChange: handleWidthChange && handleWidthChange(index),
              handleHeightChange: handleHeightChange && handleHeightChange(index),
            };

            if (file.mediumId) {
              const {
                id,
                link,
                target,
                trash,
                url,
                filename,
                alt,
                title,
                decorative,
                width,
                height,
              } = file;

              const image = (
                <img
                  src={url}
                  alt="thumbnail"
                />
              );

              return (
                <ImageUploadRow
                  key={id}
                  name={filename}
                  alt={alt}
                  image={image}
                  title={title}
                  decorative={decorative}
                  link={link}
                  target={target || '_self'}
                  trash={trash}
                  width={width}
                  height={height}
                  {...editorProps}
                />
              );
            }

            if (file.videoEmbedCode) {
              return (
                <VideoUploadRow
                  handleVideoEmbedChange={handleVideoEmbedChange(index)}
                  {...file}
                  {...editorProps}
                />
              );
            }

            const { link, target, alt, title, width, height, decorative } = file;

            const name = file.file.split('/')[1];

            const defaultTarget = target || '_self';
            const image = <img src={resolveContentUrl(file.file)} alt="thumbnail" />;

            return (
              <ImageUploadRow
                name={name}
                alt={alt}
                image={image}
                link={link}
                index={index}
                title={title}
                target={defaultTarget}
                width={width}
                height={height}
                decorative={decorative}
                {...editorProps}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

Media.propTypes = propTypes;
Media.defaultProps = defaultProps;

Media.fragments = {};

export default Media;
